/** 台北業務時間：輸入與年月日時分秒固定台北，時間戳及 ISO 序列化保留實際時間點。 */
const OFFSET_MS = 8 * 60 * 60 * 1000;

/** 將無時區的 ISO 日期／時間解讀為台北時間；帶時區及時間戳維持原時間點。 */
function normalizeInput(value) {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `${text}T00:00:00+08:00`;
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(text)) {
    return `${text.replace(" ", "T")}+08:00`;
  }
  return value;
}

/** 可替代業務 Date 建構與本地欄位操作，避免瀏覽器或 SSR 主機時區影響日期。 */
export class TaipeiDate extends Date {
  constructor(...args) {
    if (args.length === 0) super();
    else if (args.length === 1) super(normalizeInput(args[0]));
    else {
      const [year, month, day = 1, hour = 0, minute = 0, second = 0, ms = 0] = args;
      super(Date.UTC(year, month, day, hour, minute, second, ms) - OFFSET_MS);
    }
  }

  /** 解析台北輸入並傳回絕對時間戳。 */
  static parse(value) {
    return new TaipeiDate(value).getTime();
  }

  /** 建立僅供取台北日期欄位的 UTC 投影。 */
  _wallTime() {
    return new Date(this.getTime() + OFFSET_MS);
  }
  /** 取得台北年份。 */
  getFullYear() {
    return this._wallTime().getUTCFullYear();
  }
  /** 取得台北月份索引。 */
  getMonth() {
    return this._wallTime().getUTCMonth();
  }
  /** 取得台北日期。 */
  getDate() {
    return this._wallTime().getUTCDate();
  }
  /** 取得台北星期索引。 */
  getDay() {
    return this._wallTime().getUTCDay();
  }
  /** 取得台北小時。 */
  getHours() {
    return this._wallTime().getUTCHours();
  }
  /** 取得台北分鐘。 */
  getMinutes() {
    return this._wallTime().getUTCMinutes();
  }
  /** 取得台北秒。 */
  getSeconds() {
    return this._wallTime().getUTCSeconds();
  }
  /** 取得台北毫秒。 */
  getMilliseconds() {
    return this._wallTime().getUTCMilliseconds();
  }
  /** 取得台北時區偏移分鐘。 */
  getTimezoneOffset() {
    return -480;
  }

  /** 依台北牆上時間修改欄位，並回寫同一個時間點物件。 */
  _setWallTime(method, args) {
    const wall = this._wallTime();
    wall[method](...args);
    return this.setTime(wall.getTime() - OFFSET_MS);
  }
  /** 修改台北年份並保留其他欄位。 */
  setFullYear(...args) {
    return this._setWallTime("setUTCFullYear", args);
  }
  /** 修改台北月份並保留其他欄位。 */
  setMonth(...args) {
    return this._setWallTime("setUTCMonth", args);
  }
  /** 修改台北日期並保留其他欄位。 */
  setDate(...args) {
    return this._setWallTime("setUTCDate", args);
  }
  /** 修改台北小時並保留其他欄位。 */
  setHours(...args) {
    return this._setWallTime("setUTCHours", args);
  }
  /** 修改台北分鐘並保留其他欄位。 */
  setMinutes(...args) {
    return this._setWallTime("setUTCMinutes", args);
  }
  /** 修改台北秒並保留其他欄位。 */
  setSeconds(...args) {
    return this._setWallTime("setUTCSeconds", args);
  }
  /** 修改台北毫秒並保留其他欄位。 */
  setMilliseconds(...args) {
    return this._setWallTime("setUTCMilliseconds", args);
  }

  /** 以台北時區輸出文字，保留呼叫端要求的語系與顯示欄位。 */
  toLocaleString(locale, options = {}) {
    return super.toLocaleString(locale, { ...options, timeZone: "Asia/Taipei" });
  }
  /** 以台北時區輸出日期文字。 */
  toLocaleDateString(locale, options = {}) {
    return super.toLocaleDateString(locale, { ...options, timeZone: "Asia/Taipei" });
  }
  /** 以台北時區輸出時間文字。 */
  toLocaleTimeString(locale, options = {}) {
    return super.toLocaleTimeString(locale, { ...options, timeZone: "Asia/Taipei" });
  }

  /** 輸出台北時間文字供記錄與預設文字顯示。 */
  toTimeString() {
    return `${this.toTaipeiInputString().slice(11)}:${String(this.getSeconds()).padStart(2, "0")} GMT+0800 (Asia/Taipei)`;
  }
  /** 輸出台北日期文字。 */
  toDateString() {
    return this.toTaipeiDateString();
  }
  /** 輸出台北日期與時間文字。 */
  toString() {
    return Number.isNaN(this.getTime()) ? "Invalid Date" : `${this.toTaipeiDateString()} ${this.toTimeString()}`;
  }

  /** 台北日期／表單時間；ISO 傳輸請維持 toISOString()。 */
  toTaipeiDateString() {
    return `${String(this.getFullYear()).padStart(4, "0")}-${String(this.getMonth() + 1).padStart(2, "0")}-${String(this.getDate()).padStart(2, "0")}`;
  }
  /** 輸出台北日期時間供表單使用。 */
  toTaipeiInputString() {
    return `${this.toTaipeiDateString()}T${String(this.getHours()).padStart(2, "0")}:${String(this.getMinutes()).padStart(2, "0")}`;
  }
}
